// AddressSelector — saved-address picker with fallback to AddressForm.
//
// On mount: listAddresses(). Non-empty → selectable list (pre-selects
// default address); selecting yields that address id. Empty → renders
// AddressForm directly. List-fetch error → falls back to AddressForm
// (does NOT hard-block checkout). Resolves a single address id upward.
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens(); the saved
// addresses render as token-styled selectable radio cards.
//
// RESTYLE ONLY: the load/select/new-entry/fallback flow, the listAddresses
// wiring, and the resolved id+address payload are unchanged — only the
// presentation layer was migrated onto theme tokens + primitives. The card
// selection now exposes proper radiogroup/radio + aria-checked semantics
// with arrow-key + Space/Enter keyboard support.

import { useState, useEffect, useCallback } from "react";
import {
  useEmbedTokens,
  EmbedButton,
  EmbedHeading,
  EmbedText,
  EmbedLink,
} from "../../embed-shared/primitives";
import { listAddresses } from "./address-api";
import type { AddressWire } from "./address-api";
import { AddressForm } from "./AddressForm";

export interface AddressSelectorProps {
  /** "shipping" or "billing" — passed to AddressForm for new-entry. */
  variant: "shipping" | "billing";
  /** Called with the chosen address id AND the full address object.
   *  Passing the full object avoids a replica-lag re-fetch (project rule #3):
   *  the caller uses the already-loaded address for display instead of
   *  re-reading from a read-replica that may not yet have the new row. */
  onAddressSelected: (addressId: number, address: AddressWire) => void;
  /** Called when the user clicks "Back". */
  onBack?: () => void;
}

type SelectorState =
  | { status: "loading" }
  | { status: "list"; addresses: AddressWire[]; selectedId: number }
  | { status: "new-entry" };

export function AddressSelector({ variant, onAddressSelected, onBack }: AddressSelectorProps) {
  const t = useEmbedTokens();
  const [state, setState] = useState<SelectorState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const addresses = await listAddresses();
        if (cancelled) return;
        if (addresses.length === 0) {
          setState({ status: "new-entry" });
        } else {
          // Pre-select the default address, or the first one.
          const defaultAddr = addresses.find((a) => a.is_default);
          setState({
            status: "list",
            addresses,
            selectedId: defaultAddr?.id ?? addresses[0].id,
          });
        }
      } catch {
        // Fetch error → fall back to new-address entry (do NOT block checkout).
        if (!cancelled) {
          setState({ status: "new-entry" });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelect = useCallback((id: number) => {
    setState((prev) => {
      if (prev.status !== "list") return prev;
      return { ...prev, selectedId: id };
    });
  }, []);

  // Arrow-key navigation across the radio cards (roving selection).
  const handleKeyNav = useCallback((e: React.KeyboardEvent) => {
    setState((prev) => {
      if (prev.status !== "list") return prev;
      const idx = prev.addresses.findIndex((a) => a.id === prev.selectedId);
      if (idx < 0) return prev;
      let nextIdx = idx;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        nextIdx = (idx + 1) % prev.addresses.length;
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        nextIdx = (idx - 1 + prev.addresses.length) % prev.addresses.length;
      } else {
        return prev;
      }
      return { ...prev, selectedId: prev.addresses[nextIdx].id };
    });
  }, []);

  const handleUseSelected = useCallback(() => {
    if (state.status === "list") {
      const addr = state.addresses.find((a) => a.id === state.selectedId);
      if (addr) {
        onAddressSelected(state.selectedId, addr);
      }
    }
  }, [state, onAddressSelected]);

  const handleSwitchToNew = useCallback(() => {
    setState({ status: "new-entry" });
  }, []);

  // Loading state.
  if (state.status === "loading") {
    return (
      <p role="status" style={{ textAlign: "center", margin: 0 }}>
        <EmbedText tone="secondary">Loading addresses...</EmbedText>
      </p>
    );
  }

  // New-address entry (empty list or user chose "Add new address").
  if (state.status === "new-entry") {
    return (
      <AddressForm
        variant={variant}
        onAddressCreated={(id, address) => onAddressSelected(id, address)}
        onBack={onBack}
      />
    );
  }

  // Saved-address list.
  return (
    <div>
      <div style={{ marginBottom: t.spacing.md }}>
        <EmbedHeading level={2}>
          {variant === "shipping" ? "Shipping address" : "Billing address"}
        </EmbedHeading>
      </div>

      <div
        role="radiogroup"
        aria-label={variant === "shipping" ? "Shipping address" : "Billing address"}
        style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}
      >
        {state.addresses.map((addr) => {
          const isSelected = addr.id === state.selectedId;
          // TD-319: stays raw. role="radio" full-width selectable card (block,
          // left-aligned, 2px selection border) — a radio, not a button;
          // EmbedButton would centre it and lose the affordance.
          return (
            <button
              key={addr.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => handleSelect(addr.id)}
              onKeyDown={handleKeyNav}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: t.spacing.md,
                border: `2px solid ${isSelected ? t.colors.primary : t.colors.border}`,
                borderRadius: t.borderRadius,
                backgroundColor: isSelected ? t.colors.primaryTint : t.colors.inputBg,
                cursor: "pointer",
                fontFamily: t.fontFamily,
                fontSize: t.fontSize.sm,
                color: t.colors.text,
              }}
            >
              <span style={{ fontWeight: 600 }}>{addr.line1}</span>
              {addr.line2 && <span>, {addr.line2}</span>}
              <br />
              <span style={{ color: t.colors.textSecondary }}>
                {addr.city}, {addr.state} {addr.postal_code}
              </span>
              {addr.is_default && (
                <span
                  style={{
                    marginLeft: t.spacing.sm,
                    fontSize: t.fontSize.xs,
                    color: t.colors.link,
                    fontWeight: 600,
                  }}
                >
                  Default
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: t.spacing.sm, padding: `${t.spacing.sm}px 0` }}>
        <EmbedLink onClick={handleSwitchToNew}>+ Add new address</EmbedLink>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.lg }}>
        {onBack && (
          <EmbedButton type="button" variant="secondary" onClick={onBack}>
            Back
          </EmbedButton>
        )}
        <EmbedButton type="button" variant="primary" fullWidth onClick={handleUseSelected}>
          Use this address
        </EmbedButton>
      </div>
    </div>
  );
}
